import { styled } from '@linaria/react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { leftPanelCollapsedQuery } from '../../../core/mapV2/queries';
import { leftPanelToggled } from '../../../core/mapV2/state';
import theme from '../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../types';
import { LayersPanel } from '../panels/layers/LayersPanel';
import { LayerStrip } from '../panels/layers/LayerStrip';

const EXPANDED_WIDTH = '320px';
const COLLAPSED_WIDTH = '56px';

const Panel = styled.aside`
    flex: 0 0 ${EXPANDED_WIDTH};
    width: ${EXPANDED_WIDTH};
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    overflow: hidden;
    background-color: ${theme.color.white.main};
    border-right: 1px solid ${theme.color.grey.medium};
    transition: width 200ms ease-out, flex-basis 200ms ease-out;

    &[data-collapsed='true'] {
        flex-basis: ${COLLAPSED_WIDTH};
        width: ${COLLAPSED_WIDTH};
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 0 0 48px;
    padding: 0 8px 0 16px;
    border-bottom: 1px solid ${theme.color.grey.medium};

    [data-collapsed='true'] > & {
        justify-content: center;
        padding: 0;
    }
`;

const Title = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${theme.color.grey.dark};
    white-space: nowrap;

    [data-collapsed='true'] & {
        display: none;
    }
`;

const Body = styled.div`
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
`;

export const LeftPanel: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const collapsed = useAppSelector(leftPanelCollapsedQuery);
    const toggleLabel = t(collapsed ? 'mapV2.leftPanel.expand' : 'mapV2.leftPanel.collapse');

    return (
        <Panel data-testid="map-v2-left-panel" data-collapsed={collapsed}>
            <Header>
                <Title>{t('mapV2.leftPanel.title')}</Title>
                <IconButton
                    size="small"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    onClick={() => dispatch(leftPanelToggled())}
                >
                    {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
            </Header>
            <Body>{collapsed ? <LayerStrip /> : <LayersPanel />}</Body>
        </Panel>
    );
};
